import { useEffect, useRef } from 'react';
import { DeckState } from '@/types/game';
import { importDeckFromBase64, decompressDeckFromUrl } from '@/utils/deckLoader';
import { calculateCenterPosition } from '@/components/tabletop/utils/positionUtils';

interface UseUrlDeckLoaderProps {
  onDeckLoad: (deck: DeckState) => void;
  onError?: (message: string) => void;
  width: number;
  height: number;
  zoomState: {
    position: { x: number; y: number };
    scale: number;
  };
}

export function useUrlDeckLoader({ onDeckLoad, onError, width, height, zoomState }: UseUrlDeckLoaderProps) {
  const hasLoadedFromUrl = useRef(false);

  useEffect(() => {
    // Only load once
    if (hasLoadedFromUrl.current) return;

    // Check for deck parameter in URL
    const urlParams = new URLSearchParams(window.location.search);
    const deckParam = urlParams.get('deck');

    if (deckParam) {
      try {
        // Calculate center position for the deck
        const centerPosition = calculateCenterPosition(width, height, zoomState.position, zoomState.scale);
        
        let deck: DeckState;
        
        // Try to detect if it's compressed format first
        try {
          deck = decompressDeckFromUrl(deckParam, centerPosition);
          console.log('Successfully loaded compressed deck from URL parameter');
        } catch (compressedError) {
          // Fall back to regular base64 format
          try {
            deck = importDeckFromBase64(deckParam, centerPosition);
            console.log('Successfully loaded regular base64 deck from URL parameter');
          } catch (base64Error) {
            throw new Error(`Failed to load deck from URL parameter. Tried both compressed and regular formats.`);
          }
        }
        
        // Load the deck
        onDeckLoad(deck);
        
        // Mark as loaded
        hasLoadedFromUrl.current = true;
        
        // Clean up the URL parameter (optional - removes the deck param from URL)
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('deck');
        window.history.replaceState({}, '', newUrl.toString());
        
      } catch (error) {
        console.error('Failed to load deck from URL parameter:', error);
        hasLoadedFromUrl.current = true;
        const reason = error instanceof Error ? error.message : 'Unknown error loading deck';
        onError?.(`Could not load shared deck: ${reason}`);
      }
    }
  }, [onDeckLoad, onError, width, height, zoomState.position, zoomState.scale]);
} 
